import type { AiChatAttachmentCreateRequest } from '@ignite/types';

const MEDIA_METADATA_TIMEOUT_MS = 10_000;

export class ChatFileMetadataError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = 'ChatFileMetadataError';
  }
}

export async function chatAttachmentCreateRequest(
  file: File,
): Promise<AiChatAttachmentCreateRequest> {
  const mimeType = file.type.trim().toLowerCase();
  if (!mimeType) throw new ChatFileMetadataError('chat.attachment_type_unsupported');
  const base = {
    filename: file.name,
    mime_type: mimeType,
    size_bytes: file.size,
  };
  if (!mimeType.startsWith('audio/') && !mimeType.startsWith('video/')) return base;
  const durationMs = await readMediaDurationMs(file, mimeType.startsWith('video/'));
  return { ...base, duration_ms: durationMs };
}

export function createAttachmentLocalId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `attachment-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function readMediaDurationMs(file: File, video: boolean): Promise<number> {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') {
    throw new ChatFileMetadataError('chat.attachment_metadata_unavailable');
  }
  const element = document.createElement(video ? 'video' : 'audio');
  const objectUrl = URL.createObjectURL(file);
  element.preload = 'metadata';
  try {
    const duration = await new Promise<number>((resolve, reject) => {
      const timer = window.setTimeout(
        () => reject(new ChatFileMetadataError('chat.attachment_metadata_unavailable')),
        MEDIA_METADATA_TIMEOUT_MS,
      );
      const finish = (callback: () => void) => {
        window.clearTimeout(timer);
        element.removeEventListener('loadedmetadata', handleLoaded);
        element.removeEventListener('error', handleError);
        callback();
      };
      const handleLoaded = () =>
        finish(() => {
          if (!Number.isFinite(element.duration) || element.duration <= 0) {
            reject(new ChatFileMetadataError('chat.attachment_metadata_invalid'));
            return;
          }
          resolve(element.duration);
        });
      const handleError = () =>
        finish(() => reject(new ChatFileMetadataError('chat.attachment_metadata_invalid')));
      element.addEventListener('loadedmetadata', handleLoaded);
      element.addEventListener('error', handleError);
      element.src = objectUrl;
    });
    return Math.max(1, Math.round(duration * 1_000));
  } finally {
    element.removeAttribute('src');
    element.load();
    URL.revokeObjectURL(objectUrl);
  }
}
